import axios from "axios";
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';

export const formService = {
  getList,
  storeOrUpdate,
  updateSchema,
  exportSubmissions,
  getSubmissions,
  getRequiredCoverage,
  getProtectedFields,
  getTemplateSchema
};

// The CURRENT schema of the library template this per-property form was adopted
// from, for "Reset to template default". Fetched on demand (a schema body
// is several KB and is only needed if the author actually resets).
// `has_template` is false when there is nothing to reset to.
function getTemplateSchema(formId){
  const none = { has_template: false, template_name: null, schema_json: null, category_global_form_id: null };
  return (
    axios
      .get(
        `${apiEndpoint.forms}/${formId}/template-schema`,
        { headers: authHeader() }
      )
      .then(res => {
        if(res && res.status === 200 && res.data && res.data.success){
          return res.data.data || none;
        }
        return none;
      })
      .catch(err => {
        console.error('Error loading the form template:', err);
        return none;
      })
  );
}

// Field keys the form's source library template marks protected — the standalone
// builder locks these against edit/delete. Resolved from the live template, so it
// also covers copies adopted before the field was locked. Returns [] on failure:
// the backend rejects a protected-field change regardless, so a failed lookup
// degrades to "nothing shown as locked", never to a silent bypass.
function getProtectedFields(formId){
  return (
    axios
      .get(
        `${apiEndpoint.forms}/${formId}/protected-fields`,
        { headers: authHeader() }
      )
      .then(res => {
        if(res && res.status === 200 && res.data && res.data.success){
          return (res.data.data && res.data.data.protected_field_keys) || [];
        }
        return [];
      })
      .catch(err => {
        console.error('Error loading protected fields:', err);
        return [];
      })
  );
}

// Which of the required form slots this property has covered, for the coverage
// warning on the Forms page. New endpoint, so the envelope is { success, data }
// rather than the legacy { code, data } the other calls here unwrap.
// Returns { slots: [], missing_count: 0 } on any failure so the banner just
// stays hidden rather than breaking the page.
function getRequiredCoverage(propertyId){
  const empty = { slots: [], missing_count: 0 };
  return (
    axios
      .get(
        `${apiEndpoint.forms}/required-coverage?property_id=${propertyId}`,
        { headers: authHeader() }
      )
      .then(res => {
        if(res && res.status === 200 && res.data && res.data.success){
          return res.data.data || empty;
        }
        return empty;
      })
      .catch(err => {
        console.error('Error loading required form coverage:', err);
        return empty;
      })
  );
}

// Targeted schema-only update (no submission side effects) — used by the
// "Edit standalone form" builder Save on an existing form.
function updateSchema(id, data){
  return (
    axios
      .put(
        `${apiEndpoint.forms}/${id}/schema`,
        data,
        { headers: authHeader() }
      )
      .then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

// `params` drives server-side paging/filtering: {page, page_size, search,
// from_date, to_date}. Omit it (as the Asset form picker does) and the endpoint
// returns every viewable form, unpaginated, exactly as before.
function getList(propertyId, params = {}){
  return (
    axios
      .get(
        `${apiEndpoint.forms}?property_id=${propertyId}`,
        {
          headers: authHeader(),
          params,
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}

function storeOrUpdate(data, id = null){
  return (id ? (
    axios
      .put(
        apiEndpointFunction.forms(id),
        data,
        {
          headers: authHeader(),
        }
      )
  ) : (
    axios
      .post(
        apiEndpoint.forms,
        data,
        {
          headers: authHeader()
        }
      )
  )).then(res => {
    if(res && res.status === 200){
      return res.data;
    }
    return {};
  });
}

// Phase 07 — list a dynamic (schema_json) form's submissions, newest first.
function getSubmissions(id){
  return (
    axios
      .get(
        `${apiEndpointFunction.forms(id)}/submissions`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
        return {};
      })
  );
}

function exportSubmissions(formId){
  return (
    axios
      .get(
        `${apiEndpoint.forms}/export-submissions?form_id=${formId}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}